import { linkHasSVG } from '@/helpers/list-page/type-guards';
import { useCalculateSlug } from '@/hooks/use-calculate-slug';
import { IMeetingPlaceLinkToAnyPage } from '@/lib/fragments/meetingPlaceLinkToAnyPage';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { PropsWithChildren } from 'react';

type LinkWrapperProps = {
  data: string | IMeetingPlaceLinkToAnyPage;
  className?: string;
  target?: string;
  rel?: string;
  itemProp?: string;
  onClick?: () => void;
};

const LinkWrapper = ({
  children,
  data,
  target,
  rel,
  className,
  itemProp,
  onClick,
}: PropsWithChildren<LinkWrapperProps>) => {
  const router = useRouter();

  const isLinkToAnyPage = (
    link: IMeetingPlaceLinkToAnyPage | string,
  ): link is IMeetingPlaceLinkToAnyPage => {
    return (
      typeof link === 'object' &&
      link !== null &&
      '__typename' in (link as IMeetingPlaceLinkToAnyPage)
    );
  };

  const isExternalLink = (link: IMeetingPlaceLinkToAnyPage): boolean => {
    return 'linkHref' in link;
  };

  const linkHref = useCalculateSlug(data);

  const getExternalLinkTarget = (link: IMeetingPlaceLinkToAnyPage) => {
    return link.linkTarget === 'New'
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};
  };

  // Prefetch on hover for faster navigation
  const handleMouseEnter = () => {
    if (linkHref) {
      router.prefetch(linkHref);
    }
  };

  const linkRel = rel ? { rel } : {};
  const linkItemProp = itemProp ? { itemProp } : {};
  const linkTarget = target ? { target } : {};
  const linkHasSvg = linkHasSVG(data);

  if (isLinkToAnyPage(data) && isExternalLink(data)) {
    return (
      <a
        className={`mb-3  ${
          linkHasSvg ? 'flex' : 'block'
        } lg:hover:text-text-light lg:hover:underline`}
        href={data?.linkHref}
        {...getExternalLinkTarget(data)}
        onClick={onClick}
      >
        {linkHasSvg && data?.linkSvg?.file?.url && (
          <Image
            src={data?.linkSvg?.file?.url}
            alt={''}
            height={16}
            width={16}
            className="h-4"
          />
        )}
        {children}
      </a>
    );
  }

  return (
    <Link
      onClick={() => {
        setTimeout(() => {
          onClick?.();
        }, 30);
      }}
      onMouseEnter={handleMouseEnter}
      href={linkHref}
      className={className}
      {...linkTarget}
      {...linkRel}
      {...linkItemProp}
    >
      {children}
    </Link>
  );
};

export default LinkWrapper;

